import { debounce } from "lodash";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { NotificationAlert } from "src/components/common";
import {
    CheckboxFormInput,
    DatePickerFormInputFabric,
    NumberFormInput,
    SelectFormInput,
    TextAreaFormInput,
    TextFormInput,
} from "src/components/form";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { RequestState } from "src/enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "src/helpers/alert";
import {
    TrialCreateBreadcrumbs,
    TrialDetailsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "src/helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import { TrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITmdState } from "src/reducers";
import { IRegistrationFormState } from "src/reducers/reactReduxForms";
import * as AccrualANZActions from "../../../actions/accrualANZ";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialActions from "../../../actions/trial";
import * as Dtos from "../../../dtos/Tmd.dtos";
import * as AuthenticationHelper from "../../../helpers/authentication";
import TrialLayout from "../TrialLayout/TrialLayout";

import "../../../styles/app.scss";

interface TrialPageProps {
    form: Dtos.Trial & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trial: Dtos.Trial;
    trials: Dtos.Trial[];
    accrualANZ: Dtos.AccrualANZ[];
    lookups: Dtos.Lookup[];
    loadingAccrualANZ: boolean;
    loadAccrualANZFailure: boolean;
    loadAccrualANZErrorCode: string;
    loadingTrial: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    savingTrial: boolean;
    saveTrialSuccess: boolean;
    saveTrialFailure: boolean;
    creatingTrial: boolean;
    createTrialSuccess: boolean;
    validatingTrial: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    saveTrial: TrialActions.ITrialSaveActionCreator;
    createTrial: TrialActions.ITrialCreateActionCreator;
    validateTrial: TrialActions.ITrialValidateActionCreator;
    loadAccrualANZ: AccrualANZActions.IAccrualANZLoadByTrialIdActionCreator;
    clearAccrualANZ: AccrualANZActions.IAccrualANZClearActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trial";

const TrialPage: FC<TrialPageProps> = ({
    form,
    formState,
    formProperties,
    trial,
    trials,
    accrualANZ,
    lookups,
    loadingAccrualANZ,
    loadAccrualANZFailure,
    loadAccrualANZErrorCode,
    loadingTrial,
    loadTrialFailure,
    loadTrialErrorCode,
    savingTrial,
    saveTrialSuccess,
    saveTrialFailure,
    creatingTrial,
    createTrialSuccess,
    validatingTrial,
    validationFailures,
    permissions,
    clearTrial,
    loadTrial,
    saveTrial,
    createTrial,
    validateTrial,
    loadAccrualANZ,
    clearAccrualANZ,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Trial = {} as Dtos.Trial;

        loadForm(reduxFormName, Object.assign({ ...trial }, extraData));
    };


    // Load trial
    useEffect(() => {
        if (trialIdNumber && !trial && !loadingTrial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialDetailsBreadcrumbs(trialIdNumber));
            loadTrial(trialIdNumber);
            loadAccrualANZ(trialIdNumber);
        } else if (!trialIdNumber && !creatingTrial && !createTrialSuccess) {
            createTrial();
            setBreadcrumbs(TrialCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trial && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trialIdNumber,
        trial,
        setDrawerItems,
        TrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialDetailsBreadcrumbs,
        loadTrial,
        loadAccrualANZ,
        createTrial,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrial,
        creatingTrial,
        createTrialSuccess,
        TrialCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadAccrualANZFailure) {
            navigate(`/error/${loadAccrualANZErrorCode}`);
        }
    }, [
        loadTrialFailure,
        loadAccrualANZFailure,
        loadTrialErrorCode,
        loadAccrualANZErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setBreadcrumbs(
                TrialDetailsBreadcrumbs(trialIdNumber, trial.displayName)
            );
        }
    }, [trialIdNumber, trial, setBreadcrumbs, TrialDetailsBreadcrumbs]);

    // Reload trial after saving
    useEffect(() => {
        if (!savingTrial && saveTrialSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/trials";
                navigate(url);
            } else if (!trialIdNumber) {
                let url: string = `/trials/${trial?.id}`;
                navigate(url);
                clearTrial();
            }
        }
    }, [
        savingTrial,
        saveTrialSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrial && saveTrialFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrial, saveTrialFailure]);

    // Cleanup effect to clear the trial on component unmount
    useEffect(() => {
        return () => {
            clearTrial();
            clearAccrualANZ();
            resetForm(reduxFormName);
        };
    }, [clearTrial, clearAccrualANZ, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/trials";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Trial) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Trial = {} as Dtos.Trial;

            if (data?.internationalTrialStatusId == 5) {
                changes.internationalDateActivated = null;
                changes.internationalCompleteDate = null;
                changes.internationalDateAccrualCompleted = null;
            }

            await saveTrial(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const calculateCurrentBCTAccrual = (trial, accrualANZ): number => {
        const monthMapping = {
            january: "01",
            february: "02",
            march: "03",
            april: "04",
            may: "05",
            june: "06",
            july: "07",
            august: "08",
            september: "09",
            october: "10",
            november: "11",
            december: "12",
        };

        if (!accrualANZ) return 0;

        const trialStartDate = Date.parse(trial?.dateActivated);

        // Flatten, filter, and sum
        return accrualANZ.reduce((total, obj) => {
            const monthlyValues = Object.entries(monthMapping)
                .map(([month, monthNum]) => {
                    const date = `${obj.year}-${monthNum}-01T00:00:00.000Z`;
                    const value = obj[month] || 0;
                    return { date: Date.parse(date), value };
                })
                .filter(({ date }) => date >= trialStartDate);

            return (
                total + monthlyValues.reduce((sum, { value }) => sum + value, 0)
            );
        }, 0);
    };

    const generateSelectOptionsFromTrials = (
        trials: Dtos.Trial[],
        currentTrialId: number
    ) => {
        if (trials && trials?.length > 0) {
            let result: any[] = [];
            result = trials.map((trial, _index) => {
                if (
                    trial.id &&
                    trial?.protocolID &&
                    trial.id != currentTrialId
                ) {
                    return {
                        label: trial.protocolID,
                        value: trial.id,
                    };
                }
            });

            result = result.filter(function (element) {
                return element !== undefined;
            });

            return result;
        }

        return [];
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Trial = {} as Dtos.Trial;

            if (form && hasBeenSubmitted && !validatingTrial) {
                await validateTrial(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const canViewDetails = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialDetailsView,
            trialIdNumber,
            undefined
        );
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialIdNumber,
                undefined,
                permissions,
                Dtos.Permission.TrialUpdate,
                Dtos.Permission.TrialCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrial ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        var sumAccrualANZ = 0;
        var sumTotalAccrual = 0;
        if (trial) {
            sumAccrualANZ = calculateCurrentBCTAccrual(form, accrualANZ);
            sumTotalAccrual =
                (form.internationalTotalAccrual ?? 0) +
                calculateCurrentBCTAccrual(trial, accrualANZ);
        }

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-xl-12 col-xxl-8">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            protocolID: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-3",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-xl-12 col-xxl-4 d-flex align-items-center w-100">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            confidentialityDeedRequired: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Confidentiality Deed Required",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 d-flex w-100",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-3",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field d-flex align-items-center",
                                                hide: !canViewDetails,
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            title: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            studyAcronym: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            institutionCodingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Trial",
                                                                "InstitutionCodingId",
                                                                lookups
                                                            ),
                                                            form.institutionCodingId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            shortTitle: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            trialTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Trial",
                                                                "TrialTypeId",
                                                                lookups
                                                            ),
                                                            form.trialTypeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            dateEndorsedBySAC: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-4",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-xxl-2 col-xl-12 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            substudyOfTrial: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Substudy",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0",
                                                labelClassName:
                                                    "col-form-label col-sm-0 d-none",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field",
                                                hide: !canViewDetails,
                                                changeAction: (
                                                    _model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    changeForm(
                                                        reduxFormName +
                                                            ".substudyOfTrial",
                                                        value
                                                    );
                                                    changeForm(
                                                        reduxFormName +
                                                            ".substudyId",
                                                        null
                                                    );
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-xxl-10 col-xl-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            substudyId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form?.substudyOfTrial,
                                                inputLabel: " ",
                                                inputProps: {
                                                    value:
                                                        form?.substudyId ??
                                                        null,
                                                    options:
                                                        generateSelectOptionsFromTrials(
                                                            trials,
                                                            trialIdNumber
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0",
                                                labelClassName:
                                                    "col-form-label col-sm-0 d-none",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            status: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Status</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            trialStatusId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Trial",
                                                                "TrialStatusId",
                                                                lookups
                                                            ),
                                                            form.trialStatusId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            internationalTrialStatusId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Trial",
                                                                "InternationalTrialStatusId",
                                                                lookups
                                                            ),
                                                            form.internationalTrialStatusId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                                changeAction: (
                                                    _model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    changeForm(
                                                        reduxFormName +
                                                            ".internationalTrialStatusId",
                                                        value
                                                    );
                                                    if (value === 5) {
                                                        changeForm(
                                                            reduxFormName +
                                                                ".internationalDateActivated",
                                                            null
                                                        );
                                                        changeForm(
                                                            reduxFormName +
                                                                ".internationalDateAccrualCompleted",
                                                            null
                                                        );
                                                        changeForm(
                                                            reduxFormName +
                                                                ".internationalCompleteDate",
                                                            null
                                                        );
                                                    }
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            dateActivated: {
                                                type: RegistrationComponent.RegistrationControl,
                                                validationMessageAbove: true,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            internationalDateActivated: {
                                                type: RegistrationComponent.RegistrationControl,
                                                validationMessageAbove: true,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    form.internationalTrialStatusId ===
                                                        5,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row px-4 py-2">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            targetDateAccrualCompleted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                validationMessageAbove: true,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            internationalDateAccrualCompleted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                validationMessageAbove: true,
                                                inputDisabled:
                                                    formDisabled ||
                                                    form.internationalTrialStatusId ===
                                                        5,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            targetAccrual: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            totalAccrual: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: true,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                                inputProps: {
                                                    value: sumTotalAccrual,
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            dateInvitationSent: {
                                                type: RegistrationComponent.RegistrationControl,
                                                validationMessageAbove: true,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            totalBCTAccrual: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: true,
                                                inputLabel: "Total BCT Accrual",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                                inputProps: {
                                                    value: sumAccrualANZ,
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            completeDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                validationMessageAbove: true,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            internationalCompleteDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                validationMessageAbove: true,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    form.internationalTrialStatusId ===
                                                        5,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName:
                                                    "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            accrual: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Notes</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            recruitmentTimeline: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-3 col-lg-12",
                                                fieldClassName:
                                                    "col-xl-9 col-lg-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            trialCoordination: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-3 col-lg-12",
                                                fieldClassName:
                                                    "col-xl-9 col-lg-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            specialNote: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-3 col-lg-12",
                                                fieldClassName:
                                                    "col-xl-9 col-lg-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            publicationAcknowledgments: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Publication Acknowledgments</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-3 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            publicationAcknowledgments: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-0",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field",
                                                inputLabel: " ",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            commentsArea: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Comments</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-3 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trial",
                                        {
                                            comments: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-0",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field",
                                                hide: !canViewDetails,
                                                inputLabel: " ",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trial",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        permissions,
        trialIdNumber,
        savingTrial,
        formState,
        calculateCurrentBCTAccrual,
        form,
        accrualANZ,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        validationFailures,
        formProperties,
        changeForm,
        handleOnSubmit,
        handleOnSubmitFailed,
        onCancel,
    ]);

    return (
        <TrialLayout
            loading={loadingTrial || loadingAccrualANZ}
            permission={Dtos.Permission.TrialView}
        >
            {renderContent}
        </TrialLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialPageProps> => {
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let accrualANZ: Dtos.AccrualANZ[] | undefined =
        state.accrualANZ.data instanceof Array
            ? state.accrualANZ.data
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trial: trial!,
        trials: state.parentTrials?.data ?? undefined,
        accrualANZ: accrualANZ!,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        savingTrial:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Pending,
        saveTrialSuccess:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Success,
        saveTrialFailure:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Failure,
        creatingTrial:
            state.trials.createState &&
            state.trials.createState.status == RequestState.Pending,
        createTrialSuccess:
            state.trials.createState &&
            state.trials.createState.status == RequestState.Success,
        loadingAccrualANZ:
            state.accrualANZ.loadState &&
            state.accrualANZ.loadState.status == RequestState.Pending,
        loadAccrualANZFailure:
            state.accrualANZ.loadState &&
            state.accrualANZ.loadState.status == RequestState.Failure,
        loadAccrualANZErrorCode: state.accrualANZ.loadState.errorCode,
        validatingTrial:
            state.trials.validateState &&
            state.trials.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.trials.lookups,
        form: state.reduxForms.trial,
        formState: state.trials.formState,
        formProperties: state.trials.formProperties,
        validationFailures: trial ? state.trials.validationFailures : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        saveTrial: bindActionCreators(TrialActions.SaveTrial, dispatch),
        createTrial: bindActionCreators(TrialActions.CreateTrial, dispatch),
        validateTrial: bindActionCreators(TrialActions.ValidateTrial, dispatch),
        loadAccrualANZ: bindActionCreators(
            AccrualANZActions.LoadAccrualANZByTrialId,
            dispatch
        ),
        clearAccrualANZ: bindActionCreators(AccrualANZActions.Clear, dispatch),
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialPage);
